import _ from 'underscore';
import { useCallback } from 'react';
import {
  AssetPropertyName,
  getUniqueAssetsFromThemes,
} from '@bingads-webui-campaign-react/copilot-common';
import { useBannerService } from '@bingads-webui/inline-copilot';
import { constants as ImageassetsConstants } from '@bingads-webui-campaign-react/image-experience-model';

const DefaultMaxAssetsToPrefill = {
  images: 5,
  headlines: 12,
  longHeadlines: 4,
  descriptions: 4,
};

const MaxAssetsToPrefillConfig = {
  Default: DefaultMaxAssetsToPrefill,
  LinkedIn: {
    images: 1,
    headlines: 1,
    longHeadlines: 1,
    descriptions: 1,
  },
};

const getMaxAssetsToPrefill = feature => _.result(MaxAssetsToPrefillConfig, feature, DefaultMaxAssetsToPrefill);

export function usePrefill({
  activity,
  applyTextAssets,
  imageExperienceAddNewImageCallback,
  logAndTrackPrefillImages,
  copilotTracking,
  cta,
  newCTACallback,
  currentCTA,
  logo,
  feature = 'Default',
  permissions,
}) {
  const { send: sendCopilotBannerNotification } = useBannerService();

  const prefill = useCallback((themes) => {
    // put banner into Applying state
    sendCopilotBannerNotification('Apply');

    const maxAssetsToPrefill = getMaxAssetsToPrefill(feature);
    let newThemes =themes;
    if (permissions && permissions.dynamic && permissions.dynamic.CopilotDKIRecommendation && feature === 'MMA') {
      const DKITheme = _.first(themes.filter(theme => theme.name === 'Asset_Theme_For_DKI'));
      const normalThemes = themes.filter(theme => theme.name !== 'Asset_Theme_For_DKI');
      if (DKITheme && DKITheme.headlines) { newThemes = [{ ...DKITheme, headlines: DKITheme.headlines.slice(0, 2) }, ...normalThemes]; }
    }
    // prefill text assets
    const newHeadlines = applyTextAssets(newThemes, 'headlines', maxAssetsToPrefill.headlines);
    if (newHeadlines) {
      copilotTracking.trackInitialSuggestions(AssetPropertyName.HEADLINE, newHeadlines);
    }
    const newLongHeadlines = applyTextAssets(themes, 'longHeadlines', maxAssetsToPrefill.longHeadlines);
    if (newLongHeadlines) {
      copilotTracking.trackInitialSuggestions(AssetPropertyName.LONG_HEADLINE, newLongHeadlines);
    }
    const newDescriptions = applyTextAssets(themes, 'descriptions', maxAssetsToPrefill.descriptions);
    if (newDescriptions) {
      copilotTracking.trackInitialSuggestions(AssetPropertyName.DESCRIPTION, newDescriptions);
    }

    // prefill images
    return new Promise((resolve) => {
      if (imageExperienceAddNewImageCallback) {
        const newImages = getUniqueAssetsFromThemes(themes, 'images', maxAssetsToPrefill.images);
        if (newImages.length > 0) {
          resolve(imageExperienceAddNewImageCallback(newImages, ImageassetsConstants.imageOptionKeys.Image));
          return;
        }
      }
      resolve({ processedImages: [] });
    }).then((response) => {
      if (response) {
        activity.trace(`Apply image from copilot prefill: ${JSON.stringify(response.processedImages)}`);

        if (response.processedImages && response.processedImages.length > 0) {
          copilotTracking.trackInitialSuggestions(AssetPropertyName.IMAGE, _.pluck(response.processedImages, 'idString'));
          const imageAssets = _.map(
            _.filter(response.processedImages, image => image && image.suggestionId && image.idString),
            image => ({
              suggestionId: image.suggestionId,
              imageId: image.idString,
            })
          );
          if (logAndTrackPrefillImages) {
            logAndTrackPrefillImages(imageAssets);
          }
        }

        if (response.errorMessage && activity) {
          const message = `Apply image from copilot prefill error: ${response.errorMessage}`;
          activity.trace(message);
        }
      }
    }).then(() => {
      if (cta && newCTACallback && currentCTA !== 'Automated') {
        newCTACallback({
          CallToAction: cta.replace(' ', ''),
          CallToActionLanguage: 'English',
        });
      }
      if (logo) {
        return imageExperienceAddNewImageCallback([{
          fullUrl: logo,
          suggestionId: -1,
        }], ImageassetsConstants.imageOptionKeys.Logo);
      }
      return Promise.resolve();
    });
  }, [sendCopilotBannerNotification, feature, applyTextAssets, copilotTracking, imageExperienceAddNewImageCallback, activity, cta, newCTACallback, currentCTA, logo]);

  return prefill;
}
